import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import AdminTemplateList from "./AdminTemplateList";
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";

const AdminTemplateDashboard = () => {
    const navigate = useNavigate();
    const [templateCounts, setTemplateCounts] = useState({
        email: 0,
        sms: 0,
        call: 0,
        total: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTemplateCounts = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch all templates
                const response = await templateService.getEcxTemplate(0, 1000); // Fetch all templates
                const templates = response.data.content;

                // Count templates by mediumType
                const counts = {
                    email: templates.filter(t => t.mediumType === 'EMAIL').length,
                    sms: templates.filter(t => t.mediumType === 'SMS').length,
                    call: templates.filter(t => t.mediumType === 'CALL').length,
                    total: templates.length
                };

                setTemplateCounts(counts);
            } catch (err) {
                console.error('Error fetching template counts:', err);
                setError('Failed to load template statistics');
            } finally {
                setLoading(false);
            }
        };

        fetchTemplateCounts();
    }, []);

    const handleCreateTemplate = (mediumType) => {
        navigate(`/admin-templates/${mediumType.toLowerCase()}`);
    };

    if (loading) {
        return (
            <Loading />
        );
    }

    if (error) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-file-earmark-medical"></i> Admin Templates</h1>
                </div>
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                    <button
                        className="btn btn-sm btn-outline-danger ms-3"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-file-earmark-medical"></i> Admin Templates</h1>
                </div>

                {/* Header with Create Buttons */}
                <div className="row mb-4 justify-content-center align-items-center">
                    <div className="col-md-8">
                        <p className="text-muted mb-0">
                            Manage your email, SMS, and call templates. Create, edit, and organize communication templates.
                        </p>
                        <small className="text-muted">
                            Total templates: <strong>{templateCounts.total}</strong>
                        </small>
                    </div>
                    <div className="col-md-4">
                        <div className="d-flex gap-2 justify-content-md-end">
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-primary" id="dropdown-create-template" className="small">
                                    <i className="bi bi-plus-circle me-1"></i> Create Template
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => handleCreateTemplate('EMAIL')}
                                        className="d-flex align-items-center"
                                    >
                                        <i className="bi bi-envelope text-primary me-2"></i>
                                        Email Template
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => handleCreateTemplate('SMS')}
                                        className="d-flex align-items-center"
                                    >
                                        <i className="bi bi-chat-dots text-success me-2"></i>
                                        SMS Template
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => handleCreateTemplate('CALL')}
                                        className="d-flex align-items-center"
                                    >
                                        <i className="bi bi-telephone text-info me-2"></i>
                                        Call Template
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>

                {/* Quick Stats Cards */}
                <div className="row mb-4">
                    <div className="col-md-4">
                        <div className="card border-primary">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="card-title text-primary">
                                            <i className="bi bi-envelope me-2"></i>
                                            Email Templates
                                        </h6>
                                        <h3 className="mb-0">{templateCounts.email}</h3>
                                        <small className="text-muted">Active templates</small>
                                    </div>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => handleCreateTemplate('EMAIL')}
                                    >
                                        <i className="bi bi-plus"></i> Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-success">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="card-title text-success">
                                            <i className="bi bi-chat-dots me-2"></i>
                                            SMS Templates
                                        </h6>
                                        <h3 className="mb-0">{templateCounts.sms}</h3>
                                        <small className="text-muted">Active templates</small>
                                    </div>
                                    <button
                                        className="btn btn-outline-success btn-sm"
                                        onClick={() => handleCreateTemplate('SMS')}
                                    >
                                        <i className="bi bi-plus"></i> Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-info">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="card-title text-info">
                                            <i className="bi bi-telephone me-2"></i>
                                            Call Templates
                                        </h6>
                                        <h3 className="mb-0">{templateCounts.call}</h3>
                                        <small className="text-muted">Active templates</small>
                                    </div>
                                    <button
                                        className="btn btn-outline-info btn-sm"
                                        onClick={() => handleCreateTemplate('CALL')}
                                    >
                                        <i className="bi bi-plus"></i> Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Template List */}
                <AdminTemplateList />
            </div>
        </>
    )
}

export default AdminTemplateDashboard;